import { ProductFamilyKindSchema } from "@redotech/redo-inventory-common/general-listing/general-listing";
import { ListingWithRedoStatusSchema } from "@redotech/redo-inventory-common/listing-status";
import { RedoListingStatusSchema } from "@redotech/redo-inventory-common/redo-listing-status";
import { supplierIdFormat } from "@redotech/redo-inventory-id";
import {
  productFamilyIdFormat,
  productIdFormat,
} from "@redotech/redo-product-id";
import { AdvancedFilterDataSchema } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export const catalogProductsSortByFields = [
  "title",
  "product-name",
  "vendor",
  "status",
  "productKind",
  "price",
  "inventory",
  "updatedAt",
] as const;

export type CatalogProductsSortByField =
  (typeof catalogProductsSortByFields)[number];

const CatalogImageSchema = z.object({
  url: z.string(),
  altText: z.string(),
  thumbhash: z.string().nullish(),
});

export const ProductCatalogItemSchema_ = z.object({
  id: productFamilyIdFormat.schema,
  title: z.string(),
  description: z.string().optional(),
  image: CatalogImageSchema.nullish(),
  vendor: z.string().optional(),
  status: RedoListingStatusSchema,
  kind: ProductFamilyKindSchema,
  currentListings: z.array(ListingWithRedoStatusSchema),
  prices: z.array(z.object({ amount: z.string(), currency: z.string() })),
  quantity: z.bigint(),
  committed: z.bigint(),
  reserved: z.bigint(),
  available: z.bigint(),
  tags: z.array(z.string()),
  updatedAt: z.date(),
  products: z.array(
    z.object({
      id: productIdFormat.schema,
      title: z.string(),
      sku: z.string(),
      vendor: z.string(),
      image: CatalogImageSchema.nullish(),
      quantity: z.bigint(),
      committed: z.bigint(),
      reserved: z.bigint(),
      available: z.bigint(),
      price: z.number().nullish(),
      cost: z.number().nullish(),
      costFractional: z.number().nullish(),
      currency: z.string().nullish(),
      supplierCost: z.number().nullish(),
      supplierCostFractional: z.number().nullish(),
      supplierCostCurrency: z.string().nullish(),
      linkedSupplierIds: z.array(supplierIdFormat.schema),
    }),
  ),
});

export const ProductCatalogItemSchema: z.ZodType<ProductCatalogItem> =
  ProductCatalogItemSchema_;
export interface ProductCatalogItem extends z.infer<
  typeof ProductCatalogItemSchema_
> {}

export const getProductCatalog = {
  name: "getProductCatalog" as const,
  input: z.object({
    pageSize: z.number().min(1).max(1500).optional().default(20),
    page: z.number().min(0).optional().default(0),
    search: z.string().optional(),
    sortBy: createEnum(catalogProductsSortByFields).optional(),
    sortDirection: createEnum(["asc", "desc"]).optional(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).optional(),
  }),
  output: z.object({
    catalogItems: z.array(ProductCatalogItemSchema),
    totalCount: z.number(),
    page: z.number(),
    pageSize: z.number(),
  }),
};
